import { useCallback } from 'react'
import { useNavigate } from 'react-router-dom'

import type { ActivationGroup } from '@/features/activationGroup/types'
import { ResourceCategory } from '@/features/activationGroup/types'
import type { PtuConflict } from '@/features/bidding/hooks/useBidOfferPtuConflicts'
import { useBidOfferPtuConflicts } from '@/features/bidding/hooks/useBidOfferPtuConflicts'
import type {
  BidVolumes,
  BulkFillMode,
  ExtendedBid,
  ExtendedBidPtu,
  OfferedAndForecastAndSuggestedPtu,
  Portfolio,
  PtuForTheOfferVolumeDataGrid,
} from '@/features/bidding/types/bid'
import type { BidHistory } from '@/features/bidding/types/bidHistory'
import { BiddingFlow } from '@/features/bidding/utils/getBiddingFlow'
import type { GroupedBidParams } from '@/features/bidding/utils/groupedBidParams/buildBiddingLinks'
import {
  buildBidConfirmationLink,
  buildBidPriceLink,
} from '@/features/bidding/utils/groupedBidParams/buildBiddingLinks'
import { buildValidationPayloadFromCurrentPtus } from '@/features/bidding/utils/volume/buildValidationPayloadFromCurrentPtus'
import { isCreateBidButtonDisabledInVolumesPage } from '@/features/bidding/utils/volume/isCreateBidButtonDisabledInVolumesPage'

import { useBidOfferActions } from './useBidOfferActions'
import { useBidOfferBidBasis } from './useBidOfferBidBasis'
import { useBidOfferBidHistories } from './useBidOfferBidHistories'
import type { UseBidOfferCapacityBidsResult } from './useBidOfferCapacityBids'
import { useBidOfferCapacityBids } from './useBidOfferCapacityBids'
import type { UseBidOfferColumnVisibilityResult } from './useBidOfferColumnVisibility'
import { useBidOfferColumnVisibility } from './useBidOfferColumnVisibility'
import { useBidOfferCurrentPtus } from './useBidOfferCurrentPtus'
import { useBidOfferDataRequirements } from './useBidOfferDataRequirements'
import { useBidOfferDerivedData } from './useBidOfferDerivedData'
import { useBidOfferEligibleActivationGroups } from './useBidOfferEligibleActivationGroups'
import type { EnrichedPtuData } from './useBidOfferEnrichedPtus'
import { useBidOfferEnrichedPtus } from './useBidOfferEnrichedPtus'
import { useBidOfferGroupParams } from './useBidOfferGroupParams'
import { useBidOfferPortfolio } from './useBidOfferPortfolio'
import type { UseBidOfferPtuExpirationResult } from './useBidOfferPtuExpiration'
import { useBidOfferPtuExpiration } from './useBidOfferPtuExpiration'
import type { UseBidOfferTabMetadataResult } from './useBidOfferTabMetadata'
import { useBidOfferTabMetadata } from './useBidOfferTabMetadata'
import { useValidateRegulationObject } from './useValidateRegulationObject'

export type BidOfferData = {
  groupParams: GroupedBidParams
  portfolio: Portfolio
  bidHistories: BidHistory[]
  eligibleActivationGroups: ActivationGroup[]
  latestDraftOrOfferedBids: ExtendedBid[]
  biddingFlow: BiddingFlow
  isSavingAllDrafts: boolean
  handleSaveAllClick: () => Promise<void>
  capacityBids: UseBidOfferCapacityBidsResult
  bidBasisByActivationGroup: Map<string, BidVolumes>
  latestNonDraftBidsByActivationGroup: Map<string, ExtendedBid | null>
  latestAcceptedBidsByActivationGroup: Map<string, ExtendedBid | null>
  tabMetadata: UseBidOfferTabMetadataResult
  enrichedPtusByActivationGroup: Map<string, EnrichedPtuData>
  currentPtusByActivationGroup: Map<string, OfferedAndForecastAndSuggestedPtu[]>
  updatePtusForActivationGroup: (activationGroupUuid: string, ptus: OfferedAndForecastAndSuggestedPtu[]) => void
  updateSinglePtu: (activationGroupUuid: string, newPtu: OfferedAndForecastAndSuggestedPtu) => void
  isCreateBidButtonDisabled: boolean
  isValidating: boolean
  handleCapacityFlowSubmit: () => Promise<void>
  handleCapacityAndPriceFlowSubmit: () => Promise<void>
  // Column visibility and PTU expiration utilities
  columnVisibility: UseBidOfferColumnVisibilityResult
  ptuExpiration: UseBidOfferPtuExpirationResult
  conflictivePtusByGroup: Map<string, PtuConflict[]>
  // Centralized form actions
  saveDraftForActivationGroup: (activationGroupUuid: string) => Promise<void>
  processBidPtuUpdate: (
    activationGroupUuid: string,
    newPtu: OfferedAndForecastAndSuggestedPtu,
  ) => Promise<OfferedAndForecastAndSuggestedPtu>
  fillAllPtusForActivationGroup: (
    activationGroupUuid: string,
    bulkMode: BulkFillMode,
    bulkFillValue: number | undefined,
  ) => Promise<void>
  copyPastPtusForActivationGroup: (activationGroupUuid: string, pastPtusToCopy: ExtendedBidPtu[]) => Promise<void>
  handleClipboardPaste: (activationGroupUuid: string, clipboardText: string) => Promise<void>
  getDataGridValues: (activationGroupUuid: string) => {
    acceptsOnlyPositiveInteger: boolean
    ptusForTheDataGridRows: PtuForTheOfferVolumeDataGrid[]
    volumeUnit: string
  }
}

type UseBidOfferDataReturn = { isLoading: true; data: null } | { isLoading: false; data: BidOfferData }

export function useBidOfferData(): UseBidOfferDataReturn {
  // 1. Get group params from URL (and redirect if missing)
  const groupParamsResult = useBidOfferGroupParams()

  // Early return if groupParams is still loading
  if (groupParamsResult.isLoading) {
    return { isLoading: true, data: null }
  }

  const groupParams = groupParamsResult.data

  // Call helper hook with guaranteed non-null groupParams
  return useBidOfferDataLoaded(groupParams)
}

/**
 * Helper hook that handles all data fetching when groupParams is guaranteed to be loaded.
 * This separation allows us to work with non-null groupParams throughout.
 */
function useBidOfferDataLoaded(groupParams: GroupedBidParams): UseBidOfferDataReturn {
  const navigate = useNavigate()

  // 2. Determine what data is required for this bid scenario
  const requirements = useBidOfferDataRequirements(groupParams)

  // 3. Fetch portfolio data
  const { portfolio, biddingFlow, isLoading: isLoadingPortfolio } = useBidOfferPortfolio(groupParams)

  // 4. Fetch eligible activation groups
  const { eligibleActivationGroups: rawEligibleActivationGroups, isLoading: isLoadingActivationGroups } =
    useBidOfferEligibleActivationGroups(groupParams)

  // 4a. Filter activation groups to only C_AND_I resource category if required
  const eligibleActivationGroups = requirements.requiresOnlyCAndIActivationGroups
    ? rawEligibleActivationGroups?.filter((ag) => ag.resourceCategory === ResourceCategory.C_AND_I)
    : rawEligibleActivationGroups

  // 5. Fetch bid histories
  const { bidHistories, isLoading: isLoadingBidHistories } = useBidOfferBidHistories(groupParams)

  // 6. Compute derived data
  const { latestDraftOrOfferedBids, latestNonDraftBidsByActivationGroup, latestAcceptedBidsByActivationGroup } =
    useBidOfferDerivedData({ bidHistories })

  // 7. Conditionally fetch capacity bids
  const capacityBids = useBidOfferCapacityBids(groupParams, { enabled: requirements.requiresCapacityBids })

  // 8. Fetch bid basis for all activation groups
  const { bidBasisByActivationGroup, isLoading: isLoadingBidBasis } = useBidOfferBidBasis(
    groupParams,
    eligibleActivationGroups || [],
  )

  // 9. Generate enriched PTU data
  const { enrichedPtusByActivationGroup } = useBidOfferEnrichedPtus({
    groupParams,
    eligibleActivationGroups: eligibleActivationGroups || [],
    bidBasisByActivationGroup,
    latestNonDraftBidsByActivationGroup,
    capacityBids,
    bidHistories: bidHistories || [],
    acceptedBidsByActivationGroup: latestAcceptedBidsByActivationGroup,
  })

  // 10. Manage current PTU state
  const { currentPtusByActivationGroup, updatePtusForActivationGroup, updateSinglePtu } =
    useBidOfferCurrentPtus(enrichedPtusByActivationGroup)

  // 11. Compute tab metadata based on current PTU values
  const tabMetadata = useBidOfferTabMetadata(eligibleActivationGroups || [], currentPtusByActivationGroup, capacityBids)

  // 11a. Compute column visibility utilities
  const columnVisibility = useBidOfferColumnVisibility(capacityBids, enrichedPtusByActivationGroup)

  // 11b. Compute PTU expiration utilities
  const ptuExpiration = useBidOfferPtuExpiration(currentPtusByActivationGroup, enrichedPtusByActivationGroup)

  // 11c. Compute PTU conflicts utilities
  const { conflictivePtusByGroup, isLoading: isLoadingConflictivePtus } = useBidOfferPtuConflicts(
    eligibleActivationGroups || [],
    groupParams,
  )

  // 12. Set up actions for the "Save drafts" button and form actions
  const {
    isSavingAllDrafts,
    handleSaveAllClick,
    saveDraftForActivationGroup,
    processBidPtuUpdate,
    fillAllPtusForActivationGroup,
    copyPastPtusForActivationGroup,
    handleClipboardPaste,
    getDataGridValues,
  } = useBidOfferActions({
    groupParams,
    eligibleActivationGroups,
    enrichedPtusByActivationGroup,
    currentPtusByActivationGroup,
    updatePtusForActivationGroup,
    updateSinglePtu,
    capacityBids,
  })

  // 13. Set up conflict validation with navigation callbacks
  const handleAcceptConflicts = useCallback(() => {
    if (!biddingFlow) return

    const navigationLink =
      biddingFlow === BiddingFlow.CAPACITY_FLOW ? buildBidConfirmationLink(groupParams) : buildBidPriceLink(groupParams)
    navigate(navigationLink)
  }, [biddingFlow, groupParams, navigate])

  const { validate, isValidating } = useValidateRegulationObject({
    eligibleActivationGroups: eligibleActivationGroups || [],
    onAcceptConflicts: handleAcceptConflicts,
  })

  // Check if still loading - include all loading states
  // Note: Only include capacityBids.isLoading if capacity bids are required
  const isLoading =
    isLoadingPortfolio ||
    isLoadingActivationGroups ||
    isLoadingBidHistories ||
    (requirements.requiresCapacityBids && capacityBids.isLoading) ||
    isLoadingBidBasis ||
    isLoadingConflictivePtus

  if (isLoading) {
    return { isLoading: true, data: null }
  }

  // 14. Create submit handlers
  const handleCapacityFlowSubmit = async () => {
    try {
      const payload = buildValidationPayloadFromCurrentPtus(latestDraftOrOfferedBids!, currentPtusByActivationGroup)
      await validate(payload)
      navigate(buildBidConfirmationLink(groupParams))
    } catch {
      // Validation conflict shown as warning with action buttons - navigation prevented
    }
  }

  const handleCapacityAndPriceFlowSubmit = async () => {
    try {
      const payload = buildValidationPayloadFromCurrentPtus(latestDraftOrOfferedBids!, currentPtusByActivationGroup)
      await validate(payload)
      navigate(buildBidPriceLink(groupParams))
    } catch {
      // Validation conflict shown as warning with action buttons - navigation prevented
    }
  }

  // 15. Calculate button disabled state
  const isCreateBidButtonDisabled = isCreateBidButtonDisabledInVolumesPage(
    latestDraftOrOfferedBids!,
    eligibleActivationGroups!,
  )

  return {
    isLoading: false,
    data: {
      groupParams,
      portfolio: portfolio!,
      bidHistories: bidHistories!,
      eligibleActivationGroups: eligibleActivationGroups!,
      latestDraftOrOfferedBids: latestDraftOrOfferedBids!,
      biddingFlow: biddingFlow!,
      isSavingAllDrafts,
      handleSaveAllClick,
      capacityBids,
      bidBasisByActivationGroup,
      latestNonDraftBidsByActivationGroup,
      latestAcceptedBidsByActivationGroup,
      tabMetadata,
      enrichedPtusByActivationGroup,
      currentPtusByActivationGroup,
      updatePtusForActivationGroup,
      updateSinglePtu,
      isCreateBidButtonDisabled,
      isValidating,
      handleCapacityFlowSubmit,
      handleCapacityAndPriceFlowSubmit,
      columnVisibility,
      ptuExpiration,
      conflictivePtusByGroup,
      // Centralized form actions
      saveDraftForActivationGroup,
      processBidPtuUpdate,
      fillAllPtusForActivationGroup,
      copyPastPtusForActivationGroup,
      handleClipboardPaste,
      getDataGridValues,
    },
  }
}
